import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Bell, Mail, MessageCircle, UserPlus, Edit, AtSign } from 'lucide-react';
import { UserNotificationPreferences } from '@/types/product-manager';
import { useAuth } from '@/context/AuthContext';
import { Timestamp } from 'firebase/firestore';

interface NotificationPreferencesProps {
  onSave?: (preferences: UserNotificationPreferences) => void;
}

const NotificationPreferences: React.FC<NotificationPreferencesProps> = ({ onSave }) => {
  const { userEmail } = useAuth();
  const [preferences, setPreferences] = useState<UserNotificationPreferences>({
    userId: userEmail || '',
    email: userEmail || '',
    preferences: {
      taskAssigned: true,
      taskUpdated: true,
      commentAdded: true,
      mentions: true,
    },
    emailNotifications: true,
    inAppNotifications: true,
    createdAt: Timestamp.now() as any,
    updatedAt: Timestamp.now() as any,
  });
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadPreferences = useCallback(async () => {
    if (!userEmail) return;
    
    try {
      const { getUserNotificationPreferences } = await import('@/services/productManagerService');
      const userPrefs = await getUserNotificationPreferences(userEmail);
      
      if (userPrefs) {
        setPreferences(userPrefs);
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      // Use default preferences if loading fails
    }
  }, [userEmail]);

  useEffect(() => {
    // Load existing preferences from Firestore
    loadPreferences();
  }, [loadPreferences]);

  const handlePreferenceChange = (key: keyof UserNotificationPreferences['preferences'], value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [key]: value
      },
      updatedAt: Timestamp.now() as any
    }));
    setSaved(false);
  };

  const handleGlobalToggle = (type: 'emailNotifications' | 'inAppNotifications', value: boolean) => {
    setPreferences(prev => ({
      ...prev,
      [type]: value,
      updatedAt: Timestamp.now() as any
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      const { saveUserNotificationPreferences } = await import('@/services/productManagerService');
      await saveUserNotificationPreferences(preferences);
      
      onSave?.(preferences);
      setSaved(true);
      
      // Reset saved state after 3 seconds
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Error saving notification preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const notificationTypes = [
    {
      key: 'taskAssigned' as const,
      title: 'Task Assigned',
      description: 'When a task is assigned to you',
      icon: UserPlus,
    },
    {
      key: 'taskUpdated' as const,
      title: 'Task Updated',
      description: 'When a task you\'re involved with is updated or its status changes',
      icon: Edit,
    },
    {
      key: 'commentAdded' as const,
      title: 'New Comments',
      description: 'When someone comments on your tasks',
      icon: MessageCircle,
    },
    {
      key: 'mentions' as const,
      title: 'Mentions',
      description: 'When someone mentions you in a comment',
      icon: AtSign,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Bell className="h-5 w-5 mr-2" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Choose how you want to be notified about Product Manager activity
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Global Settings */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Global Settings</h3>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Bell className="h-4 w-4 text-gray-500" />
              <div>
                <Label htmlFor="in-app-notifications" className="text-sm font-medium">
                  In-App Notifications
                </Label>
                <p className="text-xs text-gray-500">
                  Show notifications within the Product Manager app
                </p>
              </div>
            </div>
            <Switch
              id="in-app-notifications"
              checked={preferences.inAppNotifications}
              onCheckedChange={(checked) => handleGlobalToggle('inAppNotifications', checked)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Mail className="h-4 w-4 text-gray-500" />
              <div>
                <Label htmlFor="email-notifications" className="text-sm font-medium">
                  Email Notifications
                </Label>
                <p className="text-xs text-gray-500">
                  Send notifications to your email address
                </p>
              </div>
            </div>
            <Switch
              id="email-notifications"
              checked={preferences.emailNotifications}
              onCheckedChange={(checked) => handleGlobalToggle('emailNotifications', checked)}
            />
          </div>
        </div>

        <Separator />

        {/* Specific Notification Types */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Notification Types</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Choose which activities you want to be notified about
          </p>
          
          <div className="space-y-4">
            {notificationTypes.map((type) => {
              const IconComponent = type.icon;
              return (
                <div key={type.key} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <IconComponent className="h-4 w-4 text-gray-500" />
                    <div>
                      <Label htmlFor={type.key} className="text-sm font-medium">
                        {type.title}
                      </Label>
                      <p className="text-xs text-gray-500">
                        {type.description}
                      </p>
                    </div>
                  </div>
                  <Switch
                    id={type.key}
                    checked={preferences.preferences[type.key]}
                    onCheckedChange={(checked) => handlePreferenceChange(type.key, checked)}
                    disabled={!preferences.inAppNotifications && !preferences.emailNotifications}
                  />
                </div>
              );
            })}
          </div>
        </div>

        <Separator />

        {/* Save Button */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {saved && (
              <span className="text-green-600 dark:text-green-400">
                ✓ Preferences saved successfully
              </span>
            )}
          </div>
          <Button 
            onClick={handleSave} 
            disabled={loading || saved}
            className="bg-violet-600 hover:bg-violet-700"
          >
            {loading ? 'Saving...' : saved ? 'Saved' : 'Save Preferences'}
          </Button>
        </div>

        {/* Preview */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <h4 className="text-sm font-medium mb-2">Preview</h4>
          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <p>
              <strong>In-App:</strong> {preferences.inAppNotifications ? 'Enabled' : 'Disabled'}
            </p>
            <p>
              <strong>Email:</strong> {preferences.emailNotifications ? 'Enabled' : 'Disabled'}
            </p>
            <p>
              <strong>Active Types:</strong>{' '}
              {Object.entries(preferences.preferences)
                .filter(([_, enabled]) => enabled)
                .map(([key, _]) => 
                  notificationTypes.find(t => t.key === key)?.title
                )
                .filter(Boolean)
                .join(', ') || 'None'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationPreferences;